// ============================================
// VISITOR LIST — Tab component for Day Report
// ============================================
import { useState, useMemo } from "react";
import styles from "./DayReport.module.css";

const VISITORS_PER_PAGE = 25;

export const VisitorList = ({ visitors }) => {
    const [search, setSearch] = useState("");
    const [sort, setSort] = useState("time"); // "time" | "duration" | "pages"
    const [expandedId, setExpandedId] = useState(null);
    const [page, setPage] = useState(0);

    const filtered = useMemo(() => {
        let list = [...visitors];
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(v =>
                (v.name && v.name.toLowerCase().includes(q)) ||
                (v.email && v.email.toLowerCase().includes(q)) ||
                v.city.toLowerCase().includes(q) ||
                v.referrer.toLowerCase().includes(q) ||
                v.device.toLowerCase().includes(q) ||
                v.browser.toLowerCase().includes(q) ||
                v.entryPage.toLowerCase().includes(q)
            );
        }
        if (sort === "duration") list.sort((a, b) => b.totalDuration - a.totalDuration);
        else if (sort === "pages") list.sort((a, b) => b.pageCount - a.pageCount);
        return list;
    }, [visitors, search, sort]);

    const paginated = useMemo(() => {
        const start = page * VISITORS_PER_PAGE;
        return filtered.slice(start, start + VISITORS_PER_PAGE);
    }, [filtered, page]);

    const totalPages = Math.ceil(filtered.length / VISITORS_PER_PAGE);

    return (
        <div className={styles.tabContent}>
            {/* CONTROLS */}
            <div className={styles.visitorControls}>
                <div className={styles.visitorSearchWrap}>
                    <span className="material-symbols-outlined" style={{ fontSize: 18, color: "#8C95A4" }}>search</span>
                    <input
                        className={styles.visitorSearchInput}
                        type="text"
                        placeholder="Search by name, email, city, device, referrer..."
                        value={search}
                        onChange={e => { setSearch(e.target.value); setPage(0); }}
                    />
                    {search && (
                        <button className={styles.visitorSearchClear} onClick={() => setSearch("")}>×</button>
                    )}
                </div>
                <div className={styles.visitorSortBtns}>
                    <span style={{ fontSize: 12, color: "#8C95A4", fontWeight: 500 }}>Sort:</span>
                    {[
                        { key: "time", label: "Time" },
                        { key: "duration", label: "Duration" },
                        { key: "pages", label: "Pages" },
                    ].map(s => (
                        <button key={s.key}
                            className={`${styles.sortBtn} ${sort === s.key ? styles.sortBtnActive : ""}`}
                            onClick={() => setSort(s.key)}
                        >{s.label}</button>
                    ))}
                </div>
            </div>

            <div className={styles.visitorCount}>
                Showing {filtered.length} visitor{filtered.length !== 1 ? "s" : ""}
                {search && ` matching "${search}"`}
            </div>

            {/* LIST */}
            <div className={styles.visitorList}>
                {paginated.map((v) => (
                    <div key={v.id} className={`${styles.visitorRow} ${expandedId === v.id ? styles.visitorRowExpanded : ""}`}>
                        <div className={styles.visitorRowMain} onClick={() => setExpandedId(expandedId === v.id ? null : v.id)}>
                            <div className={styles.visitorAvatar}>
                                {v.isAnonymous ? "👤" : v.name.charAt(0).toUpperCase()}
                            </div>
                            <div className={styles.visitorInfo}>
                                <div className={styles.visitorName}>
                                    {v.isAnonymous ? "Anonymous Visitor" : v.name}
                                    {v.isNewUser && <span className={styles.newUserBadge}>NEW</span>}
                                    {v.converted && <span className={styles.convertedBadge}>CONVERTED</span>}
                                    {v.bounced && <span className={styles.bouncedBadge}>BOUNCED</span>}
                                </div>
                                <div className={styles.visitorMeta}>
                                    <span>🕐 {v.entryTime}</span>
                                    <span>📍 {v.city}, {v.country}</span>
                                    <span>{v.device === "Mobile" ? "📱" : v.device === "Tablet" ? "📲" : "💻"} {v.device}</span>
                                    <span>🌐 {v.browser}</span>
                                </div>
                            </div>
                            <div className={styles.visitorStats}>
                                <div className={styles.visitorStatItem}>
                                    <span className={styles.visitorStatLabel}>Duration</span>
                                    <span className={styles.visitorStatValue}>{v.totalDurationFormatted}</span>
                                </div>
                                <div className={styles.visitorStatItem}>
                                    <span className={styles.visitorStatLabel}>Pages</span>
                                    <span className={styles.visitorStatValue}>{v.pageCount}</span>
                                </div>
                            </div>
                            <span className={`material-symbols-outlined ${styles.visitorExpandIcon}`}>
                                {expandedId === v.id ? "expand_less" : "expand_more"}
                            </span>
                        </div>

                        {/* EXPANDED DETAIL */}
                        {expandedId === v.id && (
                            <div className={styles.visitorExpanded}>
                                <div className={styles.visitorDetailGrid}>
                                    <div className={styles.visitorDetailSection}>
                                        <h4>Visitor Info</h4>
                                        <div className={styles.detailRow}><span>Name</span><span>{v.name || "Anonymous"}</span></div>
                                        {v.email && <div className={styles.detailRow}><span>Email</span><span>{v.email}</span></div>}
                                        <div className={styles.detailRow}><span>Location</span><span>{v.city}, {v.country}</span></div>
                                        <div className={styles.detailRow}><span>Device</span><span>{v.device}</span></div>
                                        <div className={styles.detailRow}><span>Browser</span><span>{v.browser}</span></div>
                                        <div className={styles.detailRow}><span>OS</span><span>{v.os}</span></div>
                                        <div className={styles.detailRow}><span>Referrer</span><span>{v.referrer}</span></div>
                                        <div className={styles.detailRow}><span>Entry Time</span><span>{v.entryTime}</span></div>
                                        <div className={styles.detailRow}><span>Type</span><span>{v.isNewUser ? "New User" : "Returning User"}</span></div>
                                    </div>
                                    <div className={styles.visitorDetailSection}>
                                        <h4>Pages Visited ({v.pageCount})</h4>
                                        <div className={styles.pageJourney}>
                                            {v.pagesVisited.map((p, pi) => (
                                                <div key={pi} className={styles.pageJourneyStep}>
                                                    <div className={styles.pageJourneyDot}>
                                                        {pi === 0 ? "🟢" : pi === v.pagesVisited.length - 1 ? "🔴" : "🔵"}
                                                    </div>
                                                    <div className={styles.pageJourneyInfo}>
                                                        <span className={styles.pageJourneyTitle}>{p.title}</span>
                                                        <span className={styles.pageJourneyPath}>{p.path}</span>
                                                    </div>
                                                    <span className={styles.pageJourneyTime}>{p.timeFormatted}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* PAGINATION */}
            {totalPages > 1 && (
                <div className={styles.pagination}>
                    <button className={styles.pageBtn} disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                        <span className="material-symbols-outlined">chevron_left</span>
                    </button>
                    <span className={styles.pageInfo}>
                        Page {page + 1} of {totalPages}
                    </span>
                    <button className={styles.pageBtn} disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                        <span className="material-symbols-outlined">chevron_right</span>
                    </button>
                </div>
            )}
        </div>
    );
};
